import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type {
  AdvancedContentQualityInterface,
  QualityMetricsData,
  ImprovementRecommendationData,
  QualityTrendingData,
  WorkflowQualityData,
  QualityReportingData,
  QualityPolicyData,
  QualitySuggestion,
  UseAdvancedContentQualityReturn,
  QualityMetricsResponse,
  QualityRecommendationsResponse,
  QualityTrendingResponse,
  QualityReportResponse,
  QualityPolicyResponse,
  DateRange
} from '../types/advanced_content_quality_ui';

interface UseAdvancedContentQualityOptions {
  contentId?: string;
  contentType?: string;
  realTimeUpdates?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number; // milliseconds
}

export const useAdvancedContentQuality = (
  options: UseAdvancedContentQualityOptions = {}
): UseAdvancedContentQualityReturn => {
  const { authFetch } = useAuth();
  const {
    contentId,
    contentType,
    realTimeUpdates = false,
    autoRefresh = false,
    refreshInterval = 30000
  } = options;

  // State management
  const [qualityData, setQualityData] = useState<AdvancedContentQualityInterface | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // WebSocket connection for real-time updates
  const [ws, setWs] = useState<WebSocket | null>(null);

  // Initialize WebSocket connection for real-time updates
  const initializeWebSocket = useCallback(() => {
    if (!realTimeUpdates || !contentId) return;

    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/quality/${contentId}`;
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('Quality WebSocket connected');
    };

    websocket.onmessage = (event) => {
      try {
        const update = JSON.parse(event.data);
        if (update.type === 'quality_update') {
          setQualityData(prev => prev ? { ...prev, ...update.data } : null);
          setLastUpdate(new Date());
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    websocket.onerror = (error) => {
      console.error('Quality WebSocket error:', error);
    };

    websocket.onclose = () => {
      console.log('Quality WebSocket disconnected');
      // Attempt to reconnect after 5 seconds
      setTimeout(() => {
        if (realTimeUpdates) {
          initializeWebSocket();
        }
      }, 5000);
    };

    setWs(websocket);

    return () => {
      websocket.close();
    };
  }, [realTimeUpdates, contentId]);

  // Fetch quality metrics
  const fetchQualityMetrics = useCallback(async (): Promise<QualityMetricsData> => {
    const params = new URLSearchParams();
    if (contentId) params.append('content_id', contentId);
    if (contentType) params.append('content_type', contentType);

    const response = await authFetch(`/api/content/quality/dashboard/metrics?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch quality metrics: ${response.statusText}`);
    }

    const result: QualityMetricsResponse = await response.json();
    if (!result.success) {
      throw new Error('Failed to retrieve quality metrics');
    }

    return result.data;
  }, [authFetch, contentId, contentType]);

  // Fetch improvement recommendations
  const fetchRecommendations = useCallback(async (): Promise<ImprovementRecommendationData> => {
    const params = new URLSearchParams();
    if (contentId) params.append('content_id', contentId);
    if (contentType) params.append('content_type', contentType);

    const response = await authFetch(`/api/content/quality/recommendations/improvements?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch recommendations: ${response.statusText}`);
    }

    const result: QualityRecommendationsResponse = await response.json();
    if (!result.success) {
      throw new Error('Failed to retrieve recommendations');
    }

    return result.data;
  }, [authFetch, contentId, contentType]);

  // Fetch trending analytics
  const fetchTrendingAnalytics = useCallback(async (timeRange?: DateRange): Promise<QualityTrendingData> => {
    const params = new URLSearchParams();
    if (contentId) params.append('content_id', contentId);
    if (contentType) params.append('content_type', contentType);
    if (timeRange) {
      params.append('start_date', timeRange.start.toISOString());
      params.append('end_date', timeRange.end.toISOString());
    }

    const response = await authFetch(`/api/content/quality/analytics/trending?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch trending analytics: ${response.statusText}`);
    }

    const result: QualityTrendingResponse = await response.json();
    if (!result.success) {
      throw new Error('Failed to retrieve trending analytics');
    }

    return result.data;
  }, [authFetch, contentId, contentType]);

  // Fetch workflow integration data
  const fetchWorkflowData = useCallback(async (): Promise<WorkflowQualityData> => {
    const params = new URLSearchParams();
    if (contentId) params.append('content_id', contentId);

    const response = await authFetch(`/api/content/quality/workflow/status?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch workflow data: ${response.statusText}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error('Failed to retrieve workflow data');
    }

    return result.data;
  }, [authFetch, contentId]);

  // Fetch reporting data
  const fetchReportingData = useCallback(async (): Promise<QualityReportingData> => {
    const params = new URLSearchParams();
    if (contentId) params.append('content_id', contentId);
    if (contentType) params.append('content_type', contentType);

    const response = await authFetch(`/api/content/quality/reporting/comprehensive?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch reporting data: ${response.statusText}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error('Failed to retrieve reporting data');
    }

    return result.data;
  }, [authFetch, contentId, contentType]);

  // Fetch policy data
  const fetchPolicyData = useCallback(async (): Promise<QualityPolicyData> => {
    const response = await authFetch('/api/content/quality/policy/current');
    if (!response.ok) {
      throw new Error(`Failed to fetch policy data: ${response.statusText}`);
    }

    const result: QualityPolicyResponse = await response.json();
    if (!result.success) {
      throw new Error('Failed to retrieve policy data');
    }

    return result.data;
  }, [authFetch]);

  // Main refresh function - fetches all quality data
  const refreshQuality = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const [
        metrics,
        recommendations,
        trending,
        workflow,
        reporting,
        policy
      ] = await Promise.all([
        fetchQualityMetrics(),
        fetchRecommendations(),
        fetchTrendingAnalytics(),
        fetchWorkflowData(),
        fetchReportingData(),
        fetchPolicyData()
      ]);

      const newQualityData: AdvancedContentQualityInterface = {
        qualityMetrics: metrics,
        improvementRecommendations: recommendations,
        trendingAnalytics: trending,
        workflowIntegration: workflow,
        reportingSystem: reporting,
        policyManagement: policy
      };

      setQualityData(newQualityData);
      setLastUpdate(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh quality data';
      setError(errorMessage);
      console.error('Error refreshing quality data:', err);
    } finally {
      setLoading(false);
    }
  }, [
    fetchQualityMetrics,
    fetchRecommendations,
    fetchTrendingAnalytics,
    fetchWorkflowData,
    fetchReportingData,
    fetchPolicyData
  ]);

  // Update specific quality metrics
  const updateQualityMetrics = useCallback(async (metrics: Partial<QualityMetricsData>): Promise<void> => {
    if (!contentId) {
      throw new Error('Content ID is required for updating quality metrics');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/content/quality/metrics/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content_id: contentId,
          metrics
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update quality metrics: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error('Failed to update quality metrics');
      }

      // Update local state
      setQualityData(prev => prev ? {
        ...prev,
        qualityMetrics: { ...prev.qualityMetrics, ...metrics }
      } : null);

      setLastUpdate(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update quality metrics';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authFetch, contentId]);

  // Apply a quality improvement recommendation
  const applyRecommendation = useCallback(async (recommendation: QualitySuggestion): Promise<void> => {
    if (!contentId) {
      throw new Error('Content ID is required for applying recommendations');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/content/quality/recommendations/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content_id: contentId,
          recommendation_id: recommendation.id,
          auto_apply: recommendation.automationAvailable
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to apply recommendation: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error('Failed to apply recommendation');
      }

      // Refresh quality data to reflect changes
      await refreshQuality();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to apply recommendation';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authFetch, contentId, refreshQuality]);

  // Configure workflow quality settings
  const configureWorkflow = useCallback(async (config: Partial<WorkflowQualityData>): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/content/quality/workflow/configure', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content_id: contentId,
          workflow_config: config
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to configure workflow: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error('Failed to configure workflow');
      }

      // Update local state
      setQualityData(prev => prev ? {
        ...prev,
        workflowIntegration: { ...prev.workflowIntegration, ...config }
      } : null);

      setLastUpdate(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to configure workflow';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authFetch, contentId]);

  // Generate quality reports
  const generateReport = useCallback(async (type: string, parameters: Record<string, any>): Promise<any> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/content/quality/reporting/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          report_type: type,
          parameters: {
            content_id: contentId,
            content_type: contentType,
            ...parameters
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate report: ${response.statusText}`);
      }

      const result: QualityReportResponse = await response.json();
      if (!result.success) {
        throw new Error('Failed to generate report');
      }

      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate report';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authFetch, contentId, contentType]);

  // Update quality policies
  const updatePolicy = useCallback(async (policy: Partial<QualityPolicyData>): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/content/quality/policy/configure', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          policy_updates: policy
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update policy: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error('Failed to update policy');
      }

      // Update local state
      setQualityData(prev => prev ? {
        ...prev,
        policyManagement: { ...prev.policyManagement, ...policy }
      } : null);

      setLastUpdate(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update policy';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Initialize WebSocket on mount if real-time updates are enabled
  useEffect(() => {
    if (realTimeUpdates && contentId) {
      const cleanup = initializeWebSocket();
      return cleanup;
    }
  }, [initializeWebSocket, realTimeUpdates, contentId]);

  // Initial data fetch
  useEffect(() => {
    refreshQuality();
  }, [refreshQuality]);

  // Auto-refresh setup
  useEffect(() => {
    if (!autoRefresh || refreshInterval <= 0) return;

    const interval = setInterval(() => {
      if (!loading) {
        refreshQuality();
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loading, refreshQuality]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  return {
    qualityData,
    loading,
    error,
    refreshQuality,
    updateQualityMetrics,
    applyRecommendation,
    configureWorkflow,
    generateReport,
    updatePolicy
  };
};
